import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { portalNotificationsApi, type PortalNotification } from '@/services/portal-notifications'
import { cn } from '@/lib/utils'
import { useTenantTimeZone } from '@/lib/tenant-date-time'

interface PortalNotificationsMenuProps {
  serviceInstanceId: string | null | undefined
  triggerButtonClassName?: string
}

function getHistoryStorageKey(serviceInstanceId: string): string {
  return `portal_notifications_cleared_through:${serviceInstanceId}`
}

function readClearedThroughId(serviceInstanceId: string): number {
  if (!serviceInstanceId || typeof window === 'undefined') {
    return 0
  }

  try {
    const value = window.localStorage.getItem(getHistoryStorageKey(serviceInstanceId))
    if (!value) {
      return 0
    }

    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
  } catch {
    return 0
  }
}

function writeClearedThroughId(serviceInstanceId: string, value: number): void {
  if (!serviceInstanceId || typeof window === 'undefined') {
    return
  }

  try {
    if (value > 0) {
      window.localStorage.setItem(getHistoryStorageKey(serviceInstanceId), String(value))
    } else {
      window.localStorage.removeItem(getHistoryStorageKey(serviceInstanceId))
    }
  } catch {
    // Ignore local storage write failures (private mode, quota, etc.)
  }
}

function formatNotificationTime(value?: string | null, timeZone?: string | null): string {
  if (!value) {
    return 'Just now'
  }

  const created = new Date(value)
  if (Number.isNaN(created.getTime())) {
    return 'Just now'
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(created)
}

export function PortalNotificationsMenu({
  serviceInstanceId,
  triggerButtonClassName,
}: PortalNotificationsMenuProps) {
  const resolvedServiceInstanceId = String(serviceInstanceId ?? '').trim()
  // Notification times in the tenant timezone once it is known; until then the existing output.
  const notificationTimeZone = useTenantTimeZone(resolvedServiceInstanceId || null)
  const [open, setOpen] = useState(false)
  const [lastReadId, setLastReadId] = useState<number>(0)
  const [clearedThroughId, setClearedThroughId] = useState<number>(() =>
    readClearedThroughId(String(serviceInstanceId ?? '').trim()),
  )
  const lastToastSeenIdRef = useRef<number>(0)
  const hasBootstrappedRef = useRef(false)

  const { data: notifications = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['portal-notifications', resolvedServiceInstanceId],
    queryFn: async () => {
      const rows = await portalNotificationsApi.list({
        serviceInstanceId: resolvedServiceInstanceId,
        limit: 20,
      })

      return [...rows].sort((a, b) => b.id - a.id)
    },
    enabled: Boolean(resolvedServiceInstanceId),
    staleTime: 60_000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const latestId = notifications[0]?.id ?? 0

  useEffect(() => {
    setClearedThroughId(readClearedThroughId(resolvedServiceInstanceId))
  }, [resolvedServiceInstanceId])

  const visibleNotifications = useMemo(
    () => notifications.filter((item) => item.id > clearedThroughId),
    [notifications, clearedThroughId],
  )

  const latestVisibleId = visibleNotifications[0]?.id ?? 0

  useEffect(() => {
    if (!latestId) {
      return
    }

    if (!hasBootstrappedRef.current) {
      hasBootstrappedRef.current = true
      lastToastSeenIdRef.current = latestId
      setLastReadId((previous) => (previous > 0 ? previous : latestId))
      return
    }

    if (latestId <= lastToastSeenIdRef.current) {
      return
    }

    const fresh = notifications
      .filter((item) => item.id > lastToastSeenIdRef.current)
      .sort((a, b) => a.id - b.id)

    fresh.forEach((item) => {
      const title = item.title?.trim() || 'New update'
      const description = item.message?.trim() || ''
      toast(title, description ? { description } : undefined)
    })

    lastToastSeenIdRef.current = latestId
  }, [latestId, notifications])

  useEffect(() => {
    if (open && latestVisibleId > 0) {
      setLastReadId(latestVisibleId)
    }
  }, [open, latestVisibleId])

  const clearHistory = () => {
    if (!latestId) {
      return
    }

    setClearedThroughId(latestId)
    writeClearedThroughId(resolvedServiceInstanceId, latestId)
    setLastReadId(latestId)
    lastToastSeenIdRef.current = Math.max(lastToastSeenIdRef.current, latestId)
    toast.success('Notifications cleared')
  }

  const unseenCount = useMemo(() => {
    if (lastReadId <= 0) {
      return 0
    }

    return visibleNotifications.filter((item) => item.id > lastReadId).length
  }, [visibleNotifications, lastReadId])

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (nextOpen && resolvedServiceInstanceId) {
          void refetch()
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('relative h-10 w-10 rounded-lg border border-border bg-background', triggerButtonClassName)}
          aria-label="Portal notifications"
        >
          <Bell className="h-4 w-4" />
          {unseenCount > 0 ? (
            <span className="absolute -right-1 -top-1 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-semibold leading-none text-white">
              {unseenCount > 99 ? '99+' : unseenCount}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[360px] p-0">
        <DropdownMenuLabel className="flex items-center justify-between px-3 py-2">
          <span className="font-medium">Notifications</span>
          <button
            type="button"
            className="text-xs font-medium text-primary transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
            onClick={clearHistory}
            disabled={latestId <= 0 || isLoading}
          >
            Clear history
          </button>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="m-0" />

        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <p className="px-3 py-3 text-sm text-muted-foreground">Loading notifications...</p>
          ) : null}

          {!isLoading && isError ? (
            <p className="px-3 py-3 text-sm text-destructive">Unable to load notifications.</p>
          ) : null}

          {!isLoading && !isError && visibleNotifications.length === 0 ? (
            <p className="px-3 py-3 text-sm text-muted-foreground">No notifications yet.</p>
          ) : null}

          {!isLoading && !isError
            ? visibleNotifications.map((item: PortalNotification) => {
                const title = item.title?.trim() || item.event_type?.trim() || 'Update'
                const message = item.message?.trim() || 'New activity is available.'
                const isUnread = item.id > lastReadId

                return (
                  <div
                    key={item.id}
                    className={cn(
                      'border-b border-border/70 px-3 py-2 last:border-b-0',
                      isUnread ? 'bg-muted/35' : 'bg-background',
                    )}
                  >
                    <div className="mb-1 flex items-center gap-2">
                      <p className="line-clamp-1 text-sm font-medium text-foreground">{title}</p>
                      {isUnread ? <span className="h-2 w-2 rounded-full bg-primary" /> : null}
                    </div>
                    <p className="line-clamp-2 text-xs text-muted-foreground">{message}</p>
                    <p className="mt-1 text-[11px] text-muted-foreground">{formatNotificationTime(item.created_on, notificationTimeZone)}</p>
                  </div>
                )
              })
            : null}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
